import { useEffect, useState } from 'react';
import { Timer, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
import CryptoIcon from '../CryptoIcon';
import { formatPair } from '../../utils/commodities';

const fmtLeft = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  if (s >= 60) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  return `${s}s`;
};

const TrackerRow = ({ t, now }) => {
  const start = new Date(t.start_time || t.created_at).getTime();
  const end = new Date(t.end_time).getTime();
  const pct = Math.min(100, Math.max(0, ((now - start) / Math.max(1, end - start)) * 100));
  const left = end - now;
  const isBinary = t.kind === 'binary';
  return (
    <div className="px-3 py-2.5" data-testid={`mini-tracker-row-${t.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        {isBinary ? <CryptoIcon symbol={t.crypto_symbol} size={22} /> : <Sparkles className="h-5 w-5 text-teal-300" />}
        <span className="text-sm font-semibold text-white truncate">{isBinary ? formatPair(t.crypto_symbol) : 'AI Arbitrage'}</span>
        {isBinary && (
          <span className={`flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${t.prediction === 'up' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
            {t.prediction === 'up' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{(t.prediction || '').toUpperCase()}
          </span>
        )}
        <span className="ml-auto text-xs font-mono text-purple-200" data-testid={`mini-tracker-countdown-${t.id}`}>{left > 0 ? fmtLeft(left) : 'Settling…'}</span>
      </div>
      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-purple-500 to-teal-400 rounded-full transition-[width] duration-1000 ease-linear" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-[10px] text-gray-500 mt-1">
        <span>${Number(t.amount || 0).toLocaleString()}</span>
        <span>{Math.round(pct)}%</span>
      </div>
    </div>
  );
};

export const MiniTradeTracker = ({ trades }) => {
  const [open, setOpen] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  if (!trades.length) return null;
  return (
    <div className="fixed left-3 bottom-20 lg:bottom-6 z-40 w-[280px] rounded-2xl border border-purple-500/30 bg-[#0b1120]/90 backdrop-blur-xl shadow-2xl overflow-hidden animate-fade-in" data-testid="mini-trade-tracker">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="mini-tracker-toggle" className="w-full flex items-center gap-2 px-3 py-2.5 border-b border-purple-500/20 text-left">
        <Timer className="h-4 w-4 text-purple-300 animate-pulse" />
        <span className="text-sm font-bold text-white">{trades.length} trade{trades.length > 1 ? 's' : ''} running</span>
        {open ? <ChevronDown className="h-4 w-4 ml-auto text-gray-400" /> : <ChevronUp className="h-4 w-4 ml-auto text-gray-400" />}
      </button>
      {open && (
        <div className="max-h-64 overflow-y-auto divide-y divide-gray-800">
          {trades.map((t) => <TrackerRow key={t.id} t={t} now={now} />)}
        </div>
      )}
    </div>
  );
};

export default MiniTradeTracker;
